import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  DataTablePagination,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import { getFilterParams, useLaneRates } from 'hooks/useLaneRate';
import { showToast } from 'stores/ToastStore';
import WebAPIUtils from 'utils/AccessorialRequestWebAPIUtils';
import { t } from 'i18next';

import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useModal from 'hooks/useModal';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontal,
} from 'lucide-react';
import { LaneRatesCsvUploadModal } from 'components/modules/pricing/LaneRatesCsvUploadModal';

import { getColumns } from './columns';
import { EditRateSequenceModal } from './EditRateSequenceModal';
import { DataTableToolbar } from './DataTableToolbar';
import SessionStore from 'stores/SessionStore';
import { useLocalStorage } from 'hooks/useLocalStorage';

const highlighted = {
  rateCustomer: true,
  rateMarket: true,
  rateEquipmentType: true,
  rateEquipmentSize: true,
  rateHazardous: true,
  rateOrderType: true,
  rateSteamshipOrRailroad: true,
};

const paginationDefaults = {
  pageSize: 10,
  pageIndex: 0,
};

const columnVisibilityDefaults = {
  rateCustomer: false,
  rateEquipmentType: false,
  rateEquipmentSize: false,
  rateHazardous: false,
  rateOrderType: false,
  rateSteamshipOrRailroad: false,
};

export default function LaneRatesRateConditions() {
  const columns = getColumns();
  const [hideRateConditionColumns, setHideRateConditionColumns] =
    useState(true);
  const [columnVisibility, setColumnVisibility] = useState({
    ...columnVisibilityDefaults,
  });
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    ...paginationDefaults,
  });
  const { visible, show, close } = useModal();
  const [filters, setFilters] = useState([]);
  const [selectedRateSequence, setSelectedRateSequence] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [showLaneRatesCsvUploadModal, setLaneRatesCsvUploadModalIsOpen] =
    useState(false);
  const [isAbleToExport, setIsAbleToExport] = useState(true);

  const [tableState, setTableState] = useLocalStorage(
    'laneRateRateConditionTableState',
  );

  useEffect(() => {
    setFilters(tableState?.filters ?? []);
    setSorting(tableState?.sorting ?? []);
    setPagination(tableState?.pagination ?? { ...paginationDefaults });
    setColumnVisibility(
      tableState?.columnVisibility ?? { ...columnVisibilityDefaults },
    );
    setHideRateConditionColumns(tableState?.hideRateConditionColumns ?? true);
  }, []);

  useEffect(() => {
    setTableState({
      filters,
      sorting,
      pagination,
      columnVisibility,
      hideRateConditionColumns,
    });
  }, [
    filters,
    sorting,
    pagination,
    columnVisibility,
    hideRateConditionColumns,
  ]);

  const history = useHistory();

  const { data, isLoading, refetch } = useLaneRates({
    sorting,
    pagination,
    filters,
  });

  const selectedRowCount = useMemo(() => {
    return Object.keys(rowSelection).length;
  }, [rowSelection]);

  function handleFiltersChange(updater) {
    const newFilters = updater(filters);
    setFilters(newFilters);
    setRowSelection({});
    setPagination({
      ...pagination,
      pageIndex: 0,
    });
  }

  useEffect(() => {
    updateCanExport(filters);
  }, [filters]);

  function updateCanExport(filters) {
    if (!filters || !filters.length) {
      return setIsAbleToExport(true);
    }
    const filterNames = filters.map((f) => f.id);
    const allowedFilters = ['customerId', 'origin', 'destination'];
    const hasDisallowed = filterNames.some(
      (name) => !allowedFilters.includes(name),
    );

    if (hasDisallowed) {
      return setIsAbleToExport(false);
    }
    return setIsAbleToExport(true);
  }

  const isShipperLanePricingManager =
    SessionStore.isShipperLanePricingManager();
  const isCarrierLanePricingManager =
    SessionStore.isCarrierLanePricingManager();

  const isAbleToEditLaneRate = SessionStore.isAbleToEditLaneRate();
  const isAbleToUploadCSVLaneRates = SessionStore.isAbleToUploadCSVLaneRates();

  function canUpdateLaneRate(
    selectedRateSequenceCustomerType,
    isShipperLanePricingManager,
    isCarrierLanePricingManager,
    isAbleToEditLaneRate,
  ) {
    if (!isShipperLanePricingManager && !isCarrierLanePricingManager)
      return false;

    if (
      (isShipperLanePricingManager || isCarrierLanePricingManager) &&
      !(isShipperLanePricingManager && isCarrierLanePricingManager)
    ) {
      return (
        (selectedRateSequenceCustomerType === 'Shipper' &&
          isShipperLanePricingManager) ||
        (selectedRateSequenceCustomerType === 'Carrier' &&
          isCarrierLanePricingManager)
      );
    }

    return isAbleToEditLaneRate;
  }

  const canBulkEditLaneRates = useMemo(() => {
    const keys = Object.keys(rowSelection).map((key) => parseInt(key, 10));
    return (data?.laneRates ?? [])
      .filter((_, index) => keys.includes(index))
      .map((row) => row.customerType)
      .every((customerType) => {
        return canUpdateLaneRate(
          customerType,
          isShipperLanePricingManager,
          isCarrierLanePricingManager,
          isAbleToEditLaneRate,
        );
      });
  }, [
    data?.laneRates,
    rowSelection,
    isShipperLanePricingManager,
    isCarrierLanePricingManager,
    isAbleToEditLaneRate,
  ]);

  function handleRowSelectionChange(selection) {
    setRowSelection(selection);
  }

  function handleSortChange(updater) {
    const newValue = updater(sorting);
    setSorting(newValue);
    setPagination({ pageSize: pagination.pageSize, pageIndex: 0 });
    setRowSelection({});
  }

  function onEditRateSequence(laneRate) {
    setSelectedRateSequence(laneRate);
    show();
  }

  function handleResequenceRatesSuccess() {
    refetch();
    close();
  }

  function onEditLaneRate(laneRate) {
    history.push(`/lane-rates/${laneRate.id}/edit`);
  }

  function onViewLaneRate(laneRate) {
    history.push(`/lane-rates/${laneRate.id}`);
  }

  function onBulkEditRateSequence() {
    const selectedLaneRates = Object.keys(rowSelection).map((index) => {
      return {
        ...data?.laneRates?.[index],
        weeklyCommitment: data?.laneRates?.[index]?.commitment,
      };
    });
    const selectedLaneRateIds = selectedLaneRates.map((lr) => lr.id);
    const searchParams = new URLSearchParams();
    selectedLaneRateIds.forEach((id) => searchParams.append('id', id));
    const queryString = searchParams.toString();

    history.push({
      pathname: '/lane-rates/edit',
      search: `?${queryString}`,
    });
  }

  function handleAuditClick(laneRate) {
    history.push(`/lane-rates/${laneRate.id}?tab=audit`);
  }

  function handleBulkUploadClick() {
    setLaneRatesCsvUploadModalIsOpen(true);
  }

  async function exportLaneRates() {
    const searchParams = getFilterParams({
      sorting: [{ id: 'sequenceDate' }],
      pagination,
      filters,
    });

    try {
      await WebAPIUtils.exportUserLaneRatesWithConditions(searchParams);
      showToast(t('CSV_DOWNLOAD_SUCCESSFUL'), null, 'success');
    } catch (error) {
      showToast(t('CSV_DOWNLOAD_FAILED'), error, 'error');
    }
  }

  function renderViewRateCard(laneRate) {
    const requiredDoc = laneRate?.document || laneRate?.oldDocument;
    const url = requiredDoc?.signedUrl || '';
    const docMissing = Object.keys(requiredDoc).length === 0;

    if (!docMissing) {
      return (
        <DropdownMenuItem>
          <Link to={{ pathname: url }} target="_blank" className={`text-black`}>
            {t('VIEW_RATE_CARD')}
          </Link>
        </DropdownMenuItem>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <DropdownMenuItem>
                <Link
                  to={{ pathname: url }}
                  target="_blank"
                  className={`text-black ${docMissing ? 'pointer-events-none opacity-50' : ''}`}
                >
                  {t('VIEW_RATE_CARD')}
                </Link>
              </DropdownMenuItem>
            </span>
          </TooltipTrigger>
          <TooltipContent>Rate card has not been uploaded.</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  function canUpdateRow(row) {
    if (isCarrierLanePricingManager && isShipperLanePricingManager) {
      return true;
    }
    if (
      isShipperLanePricingManager &&
      row?.original?.customerType == 'Shipper'
    ) {
      return true;
    }
    if (
      isCarrierLanePricingManager &&
      row?.original?.customerType == 'Carrier'
    ) {
      return true;
    }
    return false;
  }

  const mappedColumnDefs = useMemo(() => {
    return columns.concat({
      id: 'actions',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="text-right">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-8 h-8 p-0">
                  <span className="sr-only">{t('OPEN_MENU')}</span>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t('ACTIONS')}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onViewLaneRate(payment)}>
                  View Lane Rate
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {!canUpdateRow(row) && (
                  <>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0}>
                            <DropdownMenuItem
                              disabled={!canUpdateRow(row)}
                              onClick={() => onEditRateSequence(payment)}
                            >
                              {t('EDIT_RATE_SEQUENCE')}
                            </DropdownMenuItem>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          You do not have permission to edit the rate sequence.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0}>
                            <DropdownMenuItem
                              onClick={() => onEditLaneRate(payment)}
                              disabled={!canUpdateRow(row)}
                            >
                              {t('EDIT_LANE_RATE')}
                            </DropdownMenuItem>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          You do not have permission to edit the lane rate.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </>
                )}
                {canUpdateRow(row) && (
                  <>
                    <DropdownMenuItem
                      onClick={() => onEditRateSequence(payment)}
                    >
                      {t('EDIT_RATE_SEQUENCE')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onEditLaneRate(payment)}
                      disabled={!canUpdateRow(row)}
                    >
                      {t('EDIT_LANE_RATE')}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                {renderViewRateCard(payment)}
                <DropdownMenuItem onClick={() => handleAuditClick(payment)}>
                  {t('VIEW_AUDIT_TRAIL')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    });
  }, []);

  function handlePaginationChange(selection) {
    setPagination(selection);
    setRowSelection({});
  }

  const options = {
    columns: mappedColumnDefs,
    data: data?.laneRates || [],
    enableRowSelection: (row) => canUpdateRow(row),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnVisibility,
      sorting,
      pagination,
      rowSelection,
      columnFilters: filters,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: handleSortChange,
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnFiltersChange: handleFiltersChange,
  };
  const table = useReactTable({
    ...options,
    manualSorting: true,
    manualPagination: true,
    rowCount: data?.count,
    manualFiltering: true,
  });

  useEffect(() => {
    if (hideRateConditionColumns) {
      setColumnVisibility({
        rateCustomer: false,
        rateEquipmentType: false,
        rateEquipmentSize: false,
        rateHazardous: false,
        rateOrderType: false,
        rateSteamshipOrRailroad: false,
      });
    } else {
      setColumnVisibility({
        rateCustomer: true,
        rateEquipmentType: true,
        rateEquipmentSize: true,
        rateHazardous: true,
        rateOrderType: true,
        rateSteamshipOrRailroad: true,
      });
    }
  }, [hideRateConditionColumns]);

  function renderFooter() {
    if (isLoading) {
      return null;
    }
    return <DataTablePagination table={table} />;
  }

  function onToggleRateConditionsVisibility() {
    setHideRateConditionColumns((visibility) => !visibility);
  }

  function renderHeader() {
    return (
      <CardHeader>
        <CardTitle className="text-lg font-semibold mb-4">
          {t('LANE_RATES')}
        </CardTitle>
        <div className="mt-2 flex justify-between flex-wrap">
          <div className="flex-1">
            <DataTableToolbar table={table}>
              <div className="flex justify-end">
                <div className="flex space-x-2 flex-wrap">
                  <Button
                    onClick={onToggleRateConditionsVisibility}
                    className={[
                      'h-10',
                      {
                        'bg-slate-50': !hideRateConditionColumns,
                      },
                    ]}
                    size="sm"
                    variant="outline"
                  >
                    {hideRateConditionColumns
                      ? t('EXPAND_RATE_CONDITIONS')
                      : t('COLLAPSE_RATE_CONDITIONS')}
                    {hideRateConditionColumns ? (
                      <ChevronRightIcon className="w-4 h-4" />
                    ) : (
                      <ChevronLeftIcon className="w-4 h-4" />
                    )}
                  </Button>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="h-10">
                        {t('ACTIONS')}{' '}
                        <ChevronDownIcon className="ml-2 w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!isAbleToUploadCSVLaneRates && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <DropdownMenuItem
                                  disabled={!isAbleToUploadCSVLaneRates}
                                  className={`pointer-events-none opacity-50`}
                                >
                                  {t('UPLOAD_LANE_RATES')}
                                </DropdownMenuItem>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              You do not have permission to upload.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {isAbleToUploadCSVLaneRates && (
                        <DropdownMenuItem
                          onClick={() => handleBulkUploadClick()}
                        >
                          {t('UPLOAD_LANE_RATES')}
                        </DropdownMenuItem>
                      )}

                      {!(
                        isShipperLanePricingManager ||
                        isCarrierLanePricingManager
                      ) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <DropdownMenuItem disabled>
                                  {t('ADD_LANE_RATE')}
                                </DropdownMenuItem>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              You do not have permission to add a lane rate.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {(isShipperLanePricingManager ||
                        isCarrierLanePricingManager) && (
                        <DropdownMenuItem asChild>
                          <Link to="/lane-rates/add" className="text-black">
                            {t('ADD_NEW_LANE_RATE')}
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {!isAbleToExport && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <DropdownMenuItem disabled={!isAbleToExport}>
                                  {t('EXPORT_LANE_RATES')}
                                </DropdownMenuItem>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Export only allowed for customer, origin, and
                              destination filters.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {isAbleToExport && (
                        <DropdownMenuItem onClick={exportLaneRates}>
                          {t('EXPORT_LANE_RATES')}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </DataTableToolbar>
          </div>
        </div>
        {selectedRowCount > 0 && (
          <Alert className="mt-4 text-sm bg-blue-50">
            <div className="flex justify-between items-center">
              <span>
                {selectedRowCount} {t('ROWS_SELECTED')}.
              </span>
              {!canBulkEditLaneRates && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button
                          disabled={!canBulkEditLaneRates}
                          variant="link"
                          onClick={() => onBulkEditRateSequence()}
                        >
                          {t('EDIT_LANE_RATES')}
                          <ArrowRightIcon className="ml-2 w-4 h-4" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      You do not have permission to edit.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {canBulkEditLaneRates && (
                <Button variant="link" onClick={() => onBulkEditRateSequence()}>
                  {t('EDIT_LANE_RATES')}
                  <ArrowRightIcon className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          </Alert>
        )}
      </CardHeader>
    );
  }

  return (
    <div className="px-4 py-5 sm:p-6 mx-auto">
      <Card>
        {renderHeader()}
        <CardContent className="space-y-4">
          <DataTable
            table={table}
            isLoading={isLoading}
            highlighted={highlighted}
          />
          {renderFooter()}
        </CardContent>
      </Card>
      {showLaneRatesCsvUploadModal && (
        <LaneRatesCsvUploadModal
          onClose={() => setLaneRatesCsvUploadModalIsOpen(false)}
        />
      )}
      {visible && (
        <EditRateSequenceModal
          laneRate={selectedRateSequence}
          open={visible}
          onOpenChange={close}
          onSaveSuccess={handleResequenceRatesSuccess}
        />
      )}
    </div>
  );
}
